<template>
  <div class="ai-chat-bubble-container">
    <!-- Chat Window -->
    <AIChat :show="isChatOpen" @close="closeChat" />

    <!-- Floating Button -->
    <button
      class="ai-chat-bubble"
      :class="{ 'is-open': isChatOpen, 'has-notification': hasUnreadMessages }"
      @click="toggleChat"
      :title="$t('aiAssistant') || 'Assistente IA'"
    >
      <transition name="icon-fade" mode="out-in">
        <i v-if="!isChatOpen" key="robot" class="fas fa-robot"></i>
        <i v-else key="close" class="fas fa-times"></i>
      </transition>

      <!-- Pulse animation when closed -->
      <span v-if="!isChatOpen" class="pulse-ring"></span>

      <!-- Notification badge -->
      <span v-if="hasUnreadMessages && !isChatOpen" class="notification-badge">
        {{ unreadCount }}
      </span>
    </button>

    <!-- Tooltip on first visit -->
    <transition name="tooltip-fade">
      <div v-if="showTooltip && !isChatOpen" class="ai-tooltip">
        <div class="ai-tooltip-content">
          <button class="ai-tooltip-close" @click="dismissTooltip">
            <i class="fas fa-times"></i>
          </button>
          <div class="ai-tooltip-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h6>{{ $t('aiAssistantTitle') || 'Assistente IA' }}</h6>
          <p>{{ $t('aiAssistantDescription') || 'Tire suas dúvidas sobre treinos e exercícios!' }}</p>
          <button class="btn btn-sm bg-gradient-primary" @click="openChatFromTooltip">
            {{ $t('startChat') || 'Iniciar conversa' }}
          </button>
        </div>
        <div class="ai-tooltip-arrow"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import AIChat from './AIChat.vue'

export default {
  name: 'AIChatBubble',
  components: {
    AIChat
  },
  setup() {
    const store = useStore()
    const isChatOpen = ref(false)
    const showTooltip = ref(false)
    const hasUnreadMessages = ref(false)
    const unreadCount = ref(0)

    // Check if user is logged in
    const isLoggedIn = computed(() => !!store.getters.ApiToken)

    const toggleChat = () => {
      isChatOpen.value = !isChatOpen.value
      if (isChatOpen.value) {
        hasUnreadMessages.value = false
        unreadCount.value = 0
        dismissTooltip()
      }
    }

    const closeChat = () => {
      isChatOpen.value = false
    }

    const dismissTooltip = () => {
      showTooltip.value = false
      localStorage.setItem('ai_tooltip_dismissed', 'true')
    }

    const openChatFromTooltip = () => {
      dismissTooltip()
      isChatOpen.value = true
    }

    onMounted(() => {
      // Show tooltip if not dismissed before and user is logged in
      const tooltipDismissed = localStorage.getItem('ai_tooltip_dismissed')
      if (!tooltipDismissed && isLoggedIn.value) {
        setTimeout(() => {
          showTooltip.value = true
        }, 2000)
      }
    })

    return {
      isChatOpen,
      showTooltip,
      hasUnreadMessages,
      unreadCount,
      isLoggedIn,
      toggleChat,
      closeChat,
      dismissTooltip,
      openChatFromTooltip
    }
  }
}
</script>

<style scoped>
.ai-chat-bubble-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1040;
}

.ai-chat-bubble {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(121, 40, 202, 0.4);
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-chat-bubble:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 32px rgba(121, 40, 202, 0.5);
}

.ai-chat-bubble.is-open {
  background: linear-gradient(310deg, #344767 0%, #344767 100%);
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid #7928CA;
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 22px;
  height: 22px;
  background: #f5365c;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

/* Icon transition */
.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: all 0.2s ease;
}

.icon-fade-enter-from {
  opacity: 0;
  transform: rotate(-90deg);
}

.icon-fade-leave-to {
  opacity: 0;
  transform: rotate(90deg);
}

/* Tooltip */
.ai-tooltip {
  position: absolute;
  bottom: 75px;
  right: 0;
  width: 280px;
  z-index: 1;
}

.ai-tooltip-content {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  text-align: center;
  position: relative;
}

.ai-tooltip-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border: none;
  background: #f8f9fa;
  border-radius: 50%;
  font-size: 12px;
  cursor: pointer;
  color: #67748e;
  transition: all 0.2s;
}

.ai-tooltip-close:hover {
  background: #e9ecef;
}

.ai-tooltip-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  margin: 0 auto 15px;
}

.ai-tooltip-content h6 {
  margin-bottom: 8px;
  color: #344767;
}

.ai-tooltip-content p {
  font-size: 13px;
  color: #67748e;
  margin-bottom: 15px;
}

.ai-tooltip-arrow {
  position: absolute;
  bottom: -10px;
  right: 25px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #fff;
}

/* Tooltip transition */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.3s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Responsive */
@media (max-width: 480px) {
  .ai-chat-bubble-container {
    bottom: 15px;
    right: 15px;
  }

  .ai-chat-bubble {
    width: 55px;
    height: 55px;
    font-size: 22px;
  }

  .ai-tooltip {
    right: -10px;
    width: 260px;
  }
}
</style>
